import { useContext } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { RanaHeading } from "@nens/ranalib";
import { TenantContext } from "@nens/ranalib";
import { RanaPageTitle } from "@nens/ranalib";

import { BasePage } from "@/components/base-page";
import { TenantProjectTemplate } from "@/features/project-file";
import { TenantCompanyDetails } from "@/features/tenant";
import { TenantSubscription } from "@/features/tenant";
import { TenantUserList } from "@/features/tenant-user";
import { useTenantActionAllowed } from "@/hooks/use-action-allowed-in-tenant";
import { RanaTabs } from "@/lib/rana-tabs";
import { useTenantQuery } from "@/queries/query-tenant";

export function TenantSettingsPage() {
  const tenantResponse = useTenantQuery();
  const { tenant } = useContext(TenantContext);
  const tenantName = tenantResponse.data ? tenantResponse.data.name : tenant;

  const { canReadUserList, canManageTenant } = useTenantActionAllowed();

  const intl = useIntl();

  const SETTINGS_TABS = {
    overview: {
      title: <FormattedMessage id="tab.companyDetails" defaultMessage="Company details" />,
      panel: (
        <>
          <RanaPageTitle
            showTenant
            subTitle={intl.formatMessage({
              id: "page_title.settings.company_details",
              defaultMessage: "Company details",
            })}
          />
          <TenantCompanyDetails />
        </>
      ),
    },
    users: {
      title: <FormattedMessage id="tab.users" defaultMessage="Users" />,
      panel: canReadUserList ? (
        <>
          <RanaPageTitle
            showTenant
            subTitle={intl.formatMessage({
              id: "page_title.settings.users",
              defaultMessage: "Users",
            })}
          />
          <TenantUserList />
        </>
      ) : null,
    },
    projects: {
      title: <FormattedMessage id="tab.projects" defaultMessage="Projects" />,
      panel: canManageTenant ? (
        <>
          <RanaPageTitle
            showTenant
            subTitle={intl.formatMessage({
              id: "page_title.settings.projects",
              defaultMessage: "Projects",
            })}
          />
          <TenantProjectTemplate />
        </>
      ) : null,
    },
    subscription: {
      title: <FormattedMessage id="tab.subscription" defaultMessage="Subscription" />,
      panel: (
        <>
          <RanaPageTitle
            showTenant
            subTitle={intl.formatMessage({
              id: "page_title.settings.subscription",
              defaultMessage: "Subscription",
            })}
          />
          <TenantSubscription />
        </>
      ),
    },
    billing: {
      title: <FormattedMessage id="tab.billing" defaultMessage="Billing" />,
      panel: null,
    },
    notifications: {
      title: <FormattedMessage id="tab.notifications" defaultMessage="Notifications" />,
      panel: null,
    },
  } as const;

  return (
    <BasePage activeSidebarButton="settings" pageSubTitle={undefined}>
      <RanaHeading type="h1" addDot>
        <FormattedMessage
          id="heading.settings"
          defaultMessage="Settings for {name}"
          values={{ name: tenantName }}
        />
      </RanaHeading>
      <RanaTabs isPageNav tabs={SETTINGS_TABS} tabKey="tabs" defaultTab={1} />
    </BasePage>
  );
}
